import React, { useState, useCallback, useEffect } from 'react';
import { getAppLockService } from '../../services/AppLockService';
import PinEntry from './PinEntry';
import SafewordModal from './SafewordModal';

type AuthState = 'biometric' | 'pin' | 'locked-out';

/**
 * AppLockOverlay — full-screen lock screen for the Helm native app.
 *
 * CRITICAL SAFETY INVARIANTS:
 * 1. getSafewordStatus() is checked BEFORE activateLockScreen() fires (in AppLockService).
 * 2. SafewordModal renders as a React portal INSIDE this overlay — the overlay is
 *    NEVER removed or navigated away from when the safeword modal is accessed.
 * 3. Dismissing SafewordModal leaves the lock screen fully active.
 *
 * DESIGN-SYSTEM EXEMPTION (Spec 236 Wave 2, 2026-06-09): this overlay intentionally
 * does NOT use the Modal/Sheet/Takeover primitives. It must never be user-dismissible
 * (no backdrop/Escape/X dismissal — the primitives default to dismissible), and its
 * z-[10001] must layer above all app content (primitives cap at z-[300]). Counted in
 * the adhoc_overlay ratchet baseline as a sanctioned exception.
 */
const AppLockOverlay: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [authState, setAuthState] = useState<AuthState>('biometric');
  const [biometricAttempts, setBiometricAttempts] = useState(0);
  const [safewordOpen, setSafewordOpen] = useState(false);

  const tryBiometric = useCallback(async () => {
    const svc = getAppLockService();
    const success = await svc.authenticateWithBiometric('Unlock Helm');
    if (success) {
      await svc.flushAuditQueue();
      svc.deactivateLockScreen();
      return;
    }
    const newAttempts = biometricAttempts + 1;
    setBiometricAttempts(newAttempts);
    if (newAttempts >= 3) {
      setAuthState('pin');
    }
  }, [biometricAttempts]);

  const handleActivate = useCallback(() => {
    setVisible(true);
    setAuthState('biometric');
    setBiometricAttempts(0);
    setSafewordOpen(false);
    // Slight delay so the overlay renders before biometric prompt appears
    setTimeout(() => tryBiometric(), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally omit tryBiometric — called once on activate

  const handleDeactivate = useCallback(() => {
    setVisible(false);
    setAuthState('biometric');
    setSafewordOpen(false);
  }, []);

  useEffect(() => {
    window.addEventListener('helm:lock-activate', handleActivate);
    window.addEventListener('helm:lock-deactivate', handleDeactivate);
    return () => {
      window.removeEventListener('helm:lock-activate', handleActivate);
      window.removeEventListener('helm:lock-deactivate', handleDeactivate);
    };
  }, [handleActivate, handleDeactivate]);

  const handlePinSuccess = useCallback(async () => {
    const svc = getAppLockService();
    await svc.flushAuditQueue();
    svc.deactivateLockScreen();
  }, []);

  const handleForgotPin = useCallback(() => {
    // Open recovery flow in system browser — user must be locked out to reach this
    window.open('https://helmapp.co/account/app-lock-recovery', '_blank');
  }, []);

  if (!visible) return null;

  return (
    <>
      <div className="app-lock-overlay fixed inset-0 z-[10001] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center gap-6">
        {/* Helm logo */}
        <img
          src="/favicon.ico"
          alt="Helm"
          width={80}
          height={80}
          className="rounded-2xl"
        />

        {/* Title */}
        <p className="text-white text-xl font-medium tracking-tight">Helm is locked</p>

        {/* Biometric state */}
        {authState === 'biometric' && (
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={tryBiometric}
              className="px-6 py-3 bg-white/10 text-white rounded-xl text-base font-medium border border-white/20 hover:bg-white/20 active:bg-white/30 transition-colors"
            >
              Unlock with Face ID / Touch ID
            </button>
            <button
              onClick={() => setAuthState('pin')}
              className="text-white/60 text-sm underline"
            >
              Use PIN instead
            </button>
          </div>
        )}

        {/* PIN entry state */}
        {authState === 'pin' && (
          <PinEntry
            onSuccess={handlePinSuccess}
            onForgotPin={handleForgotPin}
          />
        )}

        {/* Locked out state */}
        {authState === 'locked-out' && (
          <div className="text-white/60 text-sm text-center px-6">
            Too many attempts. Please use account recovery to reset your PIN.
          </div>
        )}

        {/* Emergency / Safeword access — low prominence, always accessible */}
        <button
          onClick={() => setSafewordOpen(true)}
          className="absolute bottom-8 text-white/40 text-sm underline hover:text-white/60 transition-colors"
        >
          Emergency / Safeword
        </button>
      </div>

      {/* SafewordModal renders as a portal over the overlay — overlay is NEVER removed */}
      <SafewordModal
        isOpen={safewordOpen}
        onClose={() => setSafewordOpen(false)}
      />
    </>
  );
};

export default AppLockOverlay;
